/**
 * TypeSelectionStep
 *
 * Step 1: Select guardian type
 */

import React from 'react';
import { Stack, Radio, Text, Paper, Group, ThemeIcon, Badge } from '@mantine/core';
import {
  IconHeartbeat,
  IconFileText,
  IconGavel,
  IconPhone,
} from '@tabler/icons-react';
import {
  GuardianType,
  getGuardianTypeLabel,
  getGuardianTypeDescription,
  getRequiredDocuments,
} from '../hooks/useGuardian';

interface TypeSelectionStepProps {
  selectedType?: GuardianType;
  onSelect: (type: GuardianType) => void;
}

const GUARDIAN_TYPE_CONFIG = [
  {
    type: GuardianType.HEALTHCARE_PROXY,
    icon: IconHeartbeat,
    color: 'red',
  },
  {
    type: GuardianType.POWER_OF_ATTORNEY,
    icon: IconFileText,
    color: 'blue',
  },
  {
    type: GuardianType.COURT_APPOINTED,
    icon: IconGavel,
    color: 'violet',
  },
  {
    type: GuardianType.EMERGENCY_CONTACT,
    icon: IconPhone,
    color: 'green',
  },
];

export function TypeSelectionStep({ selectedType, onSelect }: TypeSelectionStepProps) {
  return (
    <Stack>
      <Text size="sm" c="dimmed" mb="md">
        Select the type of guardian relationship. This determines what information
        they can access and what documents are required.
      </Text>

      <Radio.Group
        value={selectedType}
        onChange={(value) => onSelect(value as GuardianType)}
      >
        <Stack gap="md">
          {GUARDIAN_TYPE_CONFIG.map(({ type, icon: Icon, color }) => {
            const requiredDocs = getRequiredDocuments(type);
            const isSelected = selectedType === type;

            return (
              <Paper
                key={type}
                p="md"
                withBorder
                style={{
                  borderColor: isSelected ? `var(--mantine-color-${color}-6)` : undefined,
                  backgroundColor: isSelected
                    ? `var(--mantine-color-${color}-0)`
                    : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => onSelect(type)}
              >
                <Group wrap="nowrap" ta="left">
                  <Radio value={type} />
                  <ThemeIcon size="lg" variant="light" color={color}>
                    <Icon size={20} />
                  </ThemeIcon>
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={4}>
                      <Text fw={500}>{getGuardianTypeLabel(type)}</Text>
                      {type === GuardianType.EMERGENCY_CONTACT && (
                        <Badge size="xs" color="gray">
                          View Only
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed" mb="xs">
                      {getGuardianTypeDescription(type)}
                    </Text>
                    {requiredDocs.length > 0 && (
                      <Text size="xs" c="dimmed">
                        Required documents: {requiredDocs.join(', ')}
                      </Text>
                    )}
                  </div>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Radio.Group>

      <Paper p="md" bg="yellow.0" mt="md">
        <Text size="sm" fw={500} c="yellow.9" mb={4}>
          Important: No access to financial data
        </Text>
        <Text size="xs" c="yellow.8">
          Regardless of guardian type, guardians cannot access L1 fields (SSN, TIN,
          bank accounts, credit cards). This protects against financial fraud.
        </Text>
      </Paper>
    </Stack>
  );
}

export default TypeSelectionStep;
